import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import {
  BookOpen, FolderPlus, Upload, Search, Trash2, Folder, FileText, Image as ImageIcon,
  CheckCircle2, AlertCircle, Download, Tag, Users, Lock, DollarSign,
} from 'lucide-react';
import { toast } from 'sonner';
import Pricebook from './KnowledgeBasePricebook';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AUDIENCE_LABELS = {
  all: 'All Users',
  all_internal: 'All Staff',
  service: 'Service Dept.',
  new_construction: 'New Construction',
  office: 'Office',
  admin_only: 'Admins Only',
};

const fileIcon = (ct) => {
  if (!ct) return FileText;
  if (ct.startsWith('image/')) return ImageIcon;
  return FileText;
};

const fmtSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const KnowledgeBase = () => {
  const { user } = useAuth();
  const isAdmin = ['super_admin', 'admin'].includes(user?.role);
  const internalRoles = ['super_admin', 'admin', 'manager', 'service_manager', 'construction_manager', 'office_staff', 'technician', 'employee'];
  const canSeePricebook = internalRoles.includes(user?.role);

  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null);
  const [files, setFiles] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingFolders, setLoadingFolders] = useState(true);
  const [loadingFiles, setLoadingFiles] = useState(false);

  // upload
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadMeta, setUploadMeta] = useState({ title: '', description: '', tags: '', require_ack: false });
  const [uploading, setUploading] = useState(false);

  // folder dialog
  const [showFolder, setShowFolder] = useState(false);
  const [folderForm, setFolderForm] = useState({ label: '', audience: 'all_internal' });
  const [savingFolder, setSavingFolder] = useState(false);

  // ack viewer
  const [ackDialog, setAckDialog] = useState({ open: false, fileId: null, fileTitle: '', list: [], loading: false });

  useEffect(() => { loadFolders(); }, []);
  useEffect(() => { if (activeFolder !== null) loadFiles(activeFolder, search); }, [activeFolder]);

  const loadFolders = async () => {
    setLoadingFolders(true);
    try {
      const { data } = await axios.get(`${API}/kb/folders`, { withCredentials: true });
      const nonPB = data.filter((f) => !f.pricebook);
      setFolders(nonPB);
      if (!activeFolder && nonPB.length > 0) setActiveFolder(nonPB[0].key);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load folders');
    }
    setLoadingFolders(false);
  };

  const loadFiles = async (folder, q) => {
    setLoadingFiles(true);
    try {
      const params = {};
      if (folder) params.folder = folder;
      if (q) params.q = q;
      const { data } = await axios.get(`${API}/kb/files`, { withCredentials: true, params });
      setFiles(data || []);
    } catch (e) {
      toast.error('Failed to load files');
    }
    setLoadingFiles(false);
  };

  const onSearch = (e) => {
    e.preventDefault();
    loadFiles(activeFolder, search);
  };

  const onUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) { toast.error('Pick a file'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('folder_key', activeFolder);
      fd.append('title', uploadMeta.title || uploadFile.name);
      fd.append('description', uploadMeta.description || '');
      fd.append('tags', uploadMeta.tags || '');
      fd.append('require_ack', uploadMeta.require_ack ? 'true' : 'false');
      fd.append('file', uploadFile, uploadFile.name);
      await axios.post(`${API}/admin/kb/files`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('File uploaded');
      setShowUpload(false);
      setUploadFile(null);
      setUploadMeta({ title: '', description: '', tags: '', require_ack: false });
      loadFiles(activeFolder, search);
      loadFolders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
  };

  const downloadFile = async (f) => {
    try {
      const resp = await fetch(`${API}/kb/files/${f.id}/download`, { credentials: 'include' });
      if (!resp.ok) throw new Error('Download failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = f.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const acknowledge = async (f) => {
    try {
      await axios.post(`${API}/kb/acknowledge`, { file_id: f.id }, { withCredentials: true });
      toast.success(`Acknowledged: ${f.title}`);
      loadFiles(activeFolder, search);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Acknowledgement failed');
    }
  };

  const deleteFile = async (f) => {
    if (!window.confirm(`Delete "${f.title}"? This also clears its acknowledgement records.`)) return;
    try {
      await axios.delete(`${API}/admin/kb/files/${f.id}`, { withCredentials: true });
      toast.success('File deleted');
      loadFiles(activeFolder, search);
      loadFolders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const createFolder = async (e) => {
    e.preventDefault();
    setSavingFolder(true);
    try {
      await axios.post(`${API}/admin/kb/folders`, folderForm, { withCredentials: true });
      toast.success('Folder created');
      setShowFolder(false);
      setFolderForm({ label: '', audience: 'all_internal' });
      loadFolders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    }
    setSavingFolder(false);
  };

  const deleteFolder = async (key) => {
    if (!window.confirm(`Delete folder "${key}"? All its files must already be removed.`)) return;
    try {
      await axios.delete(`${API}/admin/kb/folders/${key}`, { withCredentials: true });
      toast.success('Folder deleted');
      if (activeFolder === key) setActiveFolder(null);
      loadFolders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const openAcks = async (f) => {
    setAckDialog({ open: true, fileId: f.id, fileTitle: f.title, list: [], loading: true });
    try {
      const { data } = await axios.get(`${API}/admin/kb/files/${f.id}/acks`, { withCredentials: true });
      setAckDialog((prev) => ({ ...prev, list: data || [], loading: false }));
    } catch {
      setAckDialog((prev) => ({ ...prev, loading: false }));
      toast.error('Failed to load acknowledgements');
    }
  };

  const activeFolderObj = useMemo(() => folders.find((f) => f.key === activeFolder), [folders, activeFolder]);

  return (
    <div data-testid="knowledge-base-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2 flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-[#D4AF37]" /> Knowledge Base
          </h2>
          <p className="text-[#A3A3A3]">Handbooks, policies, vehicle docs, training materials. {isAdmin && <span className="text-[#D4AF37]">Admins manage folders + uploads.</span>}</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Dialog open={showFolder} onOpenChange={setShowFolder}>
              <DialogTrigger asChild>
                <Button data-testid="new-folder-btn" variant="outline" className="border-[#262626] text-white hover:bg-[#262626]"><FolderPlus className="w-4 h-4 mr-2" /> New Folder</Button>
              </DialogTrigger>
              <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-md">
                <DialogHeader><DialogTitle>New Folder</DialogTitle></DialogHeader>
                <form onSubmit={createFolder} className="space-y-3">
                  <div>
                    <Label className="text-white">Label</Label>
                    <Input data-testid="folder-label" required value={folderForm.label} onChange={(e) => setFolderForm({ ...folderForm, label: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
                  </div>
                  <div>
                    <Label className="text-white">Audience</Label>
                    <Select value={folderForm.audience} onValueChange={(v) => setFolderForm({ ...folderForm, audience: v })}>
                      <SelectTrigger data-testid="folder-audience" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#141414] border-[#262626]">
                        <SelectItem value="all" className="text-white">All Users (incl. customers)</SelectItem>
                        <SelectItem value="all_internal" className="text-white">All Staff</SelectItem>
                        <SelectItem value="service" className="text-white">Service Department</SelectItem>
                        <SelectItem value="new_construction" className="text-white">New Construction</SelectItem>
                        <SelectItem value="office" className="text-white">Office</SelectItem>
                        <SelectItem value="admin_only" className="text-white">Admins Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button data-testid="folder-create-submit" type="submit" disabled={savingFolder} className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">{savingFolder ? 'Creating…' : 'Create Folder'}</Button>
                </form>
              </DialogContent>
            </Dialog>
            <Dialog open={showUpload} onOpenChange={(o) => { if (!o) setUploadFile(null); setShowUpload(o); }}>
              <DialogTrigger asChild>
                <Button data-testid="upload-file-btn" disabled={!activeFolder} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] disabled:opacity-40"><Upload className="w-4 h-4 mr-2" /> Upload File</Button>
              </DialogTrigger>
              <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-md">
                <DialogHeader><DialogTitle>Upload to "{activeFolderObj?.label}"</DialogTitle></DialogHeader>
                <form onSubmit={onUpload} className="space-y-3">
                  <div>
                    <Label className="text-white">File</Label>
                    <Input data-testid="upload-file-input" type="file" required onChange={(e) => setUploadFile(e.target.files?.[0] || null)} className="bg-[#0A0A0A] border-[#262626] text-white file:bg-[#D4AF37] file:text-[#0A0A0A] file:border-0 file:rounded file:mr-3 file:px-3 file:py-1" />
                    <p className="text-[#525252] text-xs mt-1">PDF, image, doc, ppt, xlsx, csv, txt — up to 25 MB.</p>
                  </div>
                  <div>
                    <Label className="text-white">Title (defaults to filename)</Label>
                    <Input data-testid="upload-title" value={uploadMeta.title} onChange={(e) => setUploadMeta({ ...uploadMeta, title: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
                  </div>
                  <div>
                    <Label className="text-white">Description</Label>
                    <Textarea data-testid="upload-description" rows={2} value={uploadMeta.description} onChange={(e) => setUploadMeta({ ...uploadMeta, description: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
                  </div>
                  <div>
                    <Label className="text-white">Tags <span className="text-[#525252] text-xs">(comma-separated)</span></Label>
                    <Input data-testid="upload-tags" value={uploadMeta.tags} onChange={(e) => setUploadMeta({ ...uploadMeta, tags: e.target.value })} placeholder="safety, ohio, 2026" className="bg-[#0A0A0A] border-[#262626] text-white" />
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch checked={uploadMeta.require_ack} onCheckedChange={(v) => setUploadMeta({ ...uploadMeta, require_ack: v })} data-testid="upload-require-ack" />
                    <span className="text-white text-sm">Require employee acknowledgement</span>
                  </div>
                  <Button data-testid="upload-submit" type="submit" disabled={uploading} className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">{uploading ? 'Uploading…' : 'Upload'}</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-4">
        {/* Folders */}
        <aside className="bg-[#141414] border border-[#262626] rounded-md p-3 h-fit">
          <p className="text-[#525252] text-xs uppercase tracking-wider px-2 mb-2">Folders</p>
          {loadingFolders ? (
            <p className="text-[#A3A3A3] text-sm p-2">Loading…</p>
          ) : folders.length === 0 ? (
            <p data-testid="folders-empty" className="text-[#A3A3A3] text-sm p-2">No folders visible to you.</p>
          ) : (
            <ul className="space-y-1">
              {folders.map((f) => {
                const active = f.key === activeFolder;
                return (
                  <li key={f.key} className="group">
                    <button
                      type="button"
                      onClick={() => setActiveFolder(f.key)}
                      data-testid={`folder-btn-${f.key}`}
                      className={`w-full flex items-center justify-between gap-2 px-2 py-2 rounded-md text-sm transition-colors ${
                        active ? 'bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30' : 'text-white hover:bg-[#1a1a1a]'
                      }`}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <Folder className="w-4 h-4 shrink-0" />
                        <span className="truncate">{f.label}</span>
                      </span>
                      <span className="flex items-center gap-1.5 text-[10px] shrink-0">
                        {f.file_count > 0 && <Badge className="bg-[#262626] text-[#A3A3A3] border-transparent px-1.5 py-0">{f.file_count}</Badge>}
                        {f.ack_required_count > 0 && <Badge className="bg-amber-500/20 text-amber-400 border-amber-700 px-1.5 py-0">{f.ack_required_count} ack</Badge>}
                      </span>
                    </button>
                    {isAdmin && !f.system && active && (
                      <button
                        type="button"
                        onClick={() => deleteFolder(f.key)}
                        data-testid={`delete-folder-${f.key}`}
                        className="text-[10px] text-red-400 hover:text-red-300 ml-8 mt-0.5"
                      >
                        Delete folder
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        {/* Files */}
        <section className="bg-[#141414] border border-[#262626] rounded-md p-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="min-w-0">
              {activeFolderObj ? (
                <>
                  <p className="text-white font-medium flex items-center gap-2"><Folder className="w-4 h-4 text-[#D4AF37]" /> {activeFolderObj.label}</p>
                  <p className="text-[#525252] text-xs flex items-center gap-1.5">
                    <Users className="w-3 h-3" /> {AUDIENCE_LABELS[activeFolderObj.audience] || activeFolderObj.audience}
                  </p>
                </>
              ) : (
                <p className="text-[#A3A3A3]">Pick a folder.</p>
              )}
            </div>
            <form onSubmit={onSearch} className="flex gap-2">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#525252]" />
                <Input data-testid="kb-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search filename / tag…" className="pl-8 bg-[#0A0A0A] border-[#262626] text-white w-48 sm:w-64" />
              </div>
              <Button type="submit" variant="outline" className="border-[#262626] text-white hover:bg-[#262626]">Search</Button>
            </form>
          </div>

          {loadingFiles ? (
            <div className="text-[#A3A3A3]">Loading files…</div>
          ) : files.length === 0 ? (
            <div data-testid="kb-empty" className="text-center text-[#A3A3A3] py-10">
              <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
              {activeFolder ? 'No files in this folder yet.' : 'Pick a folder.'}
            </div>
          ) : (
            <ul className="divide-y divide-[#262626]">
              {files.map((f) => {
                const Icon = fileIcon(f.content_type);
                return (
                  <li key={f.id} data-testid={`kb-file-${f.id}`} className="py-3 flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex gap-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-[#D4AF37]" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-white font-medium truncate">{f.title}</p>
                        <p className="text-[#A3A3A3] text-xs">{f.filename} · {fmtSize(f.size)} · uploaded by {f.uploaded_by || 'admin'}</p>
                        {f.description && <p className="text-[#A3A3A3] text-xs mt-1">{f.description}</p>}
                        <div className="flex gap-1 flex-wrap mt-1.5">
                          {(f.tags || []).map((t) => (
                            <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-[#262626] text-[#A3A3A3] flex items-center gap-1"><Tag className="w-2.5 h-2.5" /> {t}</span>
                          ))}
                          {f.require_ack && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1 ${f.acknowledged ? 'bg-green-500/15 text-green-400 border border-green-700' : 'bg-amber-500/15 text-amber-400 border border-amber-700'}`}>
                              {f.acknowledged ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                              {f.acknowledged ? 'Acknowledged' : 'Ack required'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button data-testid={`download-${f.id}`} type="button" size="sm" variant="outline" onClick={() => downloadFile(f)} className="border-[#262626] text-white hover:bg-[#262626]"><Download className="w-3.5 h-3.5 mr-1" /> Open</Button>
                      {f.require_ack && !f.acknowledged && (
                        <Button data-testid={`ack-${f.id}`} type="button" size="sm" onClick={() => acknowledge(f)} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Acknowledge</Button>
                      )}
                      {isAdmin && f.require_ack && (
                        <Button data-testid={`view-acks-${f.id}`} type="button" size="sm" variant="outline" onClick={() => openAcks(f)} className="border-[#262626] text-white hover:bg-[#262626]">View Acks</Button>
                      )}
                      {isAdmin && (
                        <Button data-testid={`delete-${f.id}`} type="button" size="sm" variant="outline" onClick={() => deleteFile(f)} className="border-red-700 text-red-400 hover:bg-red-900/30"><Trash2 className="w-3.5 h-3.5" /></Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>

      {/* iter233 — Legacy Pricebook retired. Pricing now lives in the Service Catalog
          (admin: /admin/service-catalog, field: /employee/service-catalog). Knowledge
          Base shows documents only. */}

      <Dialog open={ackDialog.open} onOpenChange={(o) => setAckDialog((prev) => ({ ...prev, open: o }))}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-lg">
          <DialogHeader><DialogTitle>Who acknowledged "{ackDialog.fileTitle}"?</DialogTitle></DialogHeader>
          {ackDialog.loading ? (
            <p className="text-[#A3A3A3]">Loading…</p>
          ) : ackDialog.list.length === 0 ? (
            <p className="text-[#A3A3A3]">Nobody has acknowledged this yet.</p>
          ) : (
            <ul className="divide-y divide-[#262626] max-h-80 overflow-y-auto">
              {ackDialog.list.map((a) => (
                <li key={a.user_id || `${a.user_name}-${a.acknowledged_at || ''}`} className="py-2 flex items-center justify-between">
                  <div>
                    <p className="text-white text-sm">{a.user_name}</p>
                    <p className="text-[#A3A3A3] text-xs">{a.user_role} · {a.user_department || '—'}</p>
                  </div>
                  <p className="text-[#525252] text-xs">{a.acknowledged_at ? new Date(a.acknowledged_at).toLocaleString() : '—'}</p>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KnowledgeBase;
